<template>
  <div class="pt-4 p-4 flex flex-col items-center min-h-screen text-light-text dark:text-dark-text">
    <AppSidebar />
    <div class="w-full max-w-6xl bg-white dark:bg-dark-box-bg rounded-lg shadow-md p-6 mt-6">
      <div class="flex justify-between">
      <button
        @click="goToPayoffs"
        class="mb-4 text-teal-700 dark:text-teal-400 hover:underline flex items-center"
      >
        ← Wróć do zakładki Rozliczeń
      </button>

      <button
        @click="goToInvoices"
        class="mb-4 text-teal-700 dark:text-teal-400 hover:underline flex items-center"
      >
        Przejdź do zakładki Plików →
      </button>
      </div>


      <div v-if="message" :class="messageType" 
          class="fixed top-0 left-0 right-0 z-5 p-4 rounded-lg mb-4 text-center 
                  bg-white dark:bg-dark-box-bg dark:text-dark-text dark:border 
                  dark:border-dark-border">
          {{ message }}
          <div class="absolute bottom-0 left-0 h-1 rounded-lg" 
              :style="{ width: progress + '%', backgroundColor: getProgressColor(), borderRadius: '8px' }">
          </div>
      </div>
      <div v-if="loading" class="text-center text-gray-500 dark:text-gray-400">Ładowanie danych firmy...</div>
      <div v-else>
        <div class="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mb-4">
          <label for="month-filter" class="text-sm font-medium text-gray-700 dark:text-gray-300">Filtruj według miesiąca:</label>
          <select
            id="month-filter"
            v-model="selectedMonthFilter"
            @change="filterOrders"
            class="block pl-3 pr-10 py-2 text-sm bg-light-bg text-light-text border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md dark:bg-dark-box-bg dark:text-dark-text dark:border-dark-border w-full sm:w-48"
          >
            <option value="current">Bieżący miesiąc</option>
            <option v-for="month in months" :key="month.value" :value="month.value">{{ month.label }}</option>
            <option value="all">Wszystkie</option>
          </select>
          <label for="year-filter" class="text-sm font-medium text-gray-700 dark:text-gray-300">Rok:</label>
          <select
            id="year-filter"
            v-model="selectedYear"
            @change="filterOrders"
            class="block pl-3 pr-10 py-2 text-sm bg-light-bg text-light-text border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md dark:bg-dark-box-bg dark:text-dark-text dark:border-dark-border w-full sm:w-32"
          >
            <option v-for="year in years" :key="year" :value="year">{{ year }}</option>
          </select>
          <div class="flex items-center space-x-4">
            <button
              @click="addInvoice"
              class="px-4 py-2 text-sm font-medium bg-teal-700 text-white rounded-md hover:bg-teal-800 dark:bg-teal-600 dark:hover:bg-teal-500 transition-colors"
            >
              Generuj FV ({{ currentMonthName }})
            </button>
          </div>
          <button
            @click="addSpecification"
            class="px-4 py-2 text-sm font-medium bg-blue-700 text-white rounded-md hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-500 transition-colors"
          >
            Generuj Specyfikację ({{ currentMonthName }})
          </button>
        </div>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full border-collapse border border-light-border dark:border-dark-border">
          <thead>
            <tr class="bg-light-table-header dark:bg-dark-table-header">
              <th class="border border-light-border dark:border-dark-border px-4 py-2 text-left">#</th>
              <th class="border border-light-border dark:border-dark-border px-4 py-2 text-left">Numer Zlecenia</th>
              <th class="border border-light-border dark:border-dark-border px-4 py-2 text-left">Data</th>
              <th class="border border-light-border dark:border-dark-border px-4 py-2 text-left">Trasa</th>
              <th class="border border-light-border dark:border-dark-border px-4 py-2 text-left">Pasażer</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(order, index) in paginatedOrders"
              :key="order.id"
              :class="[order.payoff ? 'bg-green-200 dark:bg-green-600' : 'bg-red-200 dark:bg-red-400', 'border-b border-gray-300 dark:border-dark-border']"
              class="cursor-pointer"
            >
              <td class="border border-light-border dark:border-dark-border px-4 py-2">{{ index + 1 }}</td>
              <td
                @click="goToProfile(order)"
                class="border border-light-border dark:border-dark-border px-4 py-2 text-teal-700 dark:text-white hover:underline">
                Zamówienie {{ order.mpk }}
              </td>
              <td class="border border-light-border dark:border-dark-border px-4 py-2">
                {{ new Date(order.date).toLocaleDateString() }}
              </td>
              <td class="border border-light-border dark:border-dark-border px-4 py-2">
                {{ order.route || 'Brak danych' }}
              </td>
              <td class="border border-light-border dark:border-dark-border px-4 py-2">
                <span v-if="order.passenger && order.passenger.length > 0">
                  {{ order.passengerNames.join(', ') }}
                </span>
                <span v-else>Brak pasażera</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="mt-4 flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
        <div class="text-center sm:text-left text-sm text-light-text dark:text-gray-400">
          Wyświetlono {{ (currentPage - 1) * itemsPerPage + paginatedOrders.length }} z {{ allOrders.length }} zleceń
        </div>

        <div class="flex flex-wrap justify-center sm:justify-end items-center gap-2">
          <button v-if="currentPage > 1"
                  @click="goToPage(1)"
                  class="px-3 py-1 text-sm font-medium bg-light-table-header rounded-md dark:bg-dark-table-header dark:text-dark-text">
            Pierwsza strona
          </button>

          <button v-if="currentPage > 1"
                  @click="prevPage"
                  class="px-3 py-1 text-sm font-medium bg-light-table-header rounded-md dark:bg-dark-table-header dark:text-dark-text">
            « Poprzednia
          </button>

          <button v-for="page in visiblePages"
                  :key="page"
                  @click="goToPage(page)"
                  :class="[currentPage === page ? 'bg-teal-700 text-white' : 'bg-gray-200 dark:bg-dark-box-bg text-gray-700 dark:text-dark-text']"
                  class="px-3 py-1 text-sm font-medium rounded-md transition-colors hover:bg-teal-700 hover:text-white dark:hover:bg-teal-600 dark:hover:text-white">
            {{ page }}
          </button>

          <button v-if="currentPage < totalPages"
                  @click="nextPage"
                  class="px-3 py-1 text-sm font-medium bg-light-table-header rounded-md dark:bg-dark-table-header dark:text-dark-text">
            Następna »
          </button>

          <button v-if="currentPage < totalPages"
                  @click="goToPage(totalPages)"
                  class="px-3 py-1 text-sm font-medium bg-light-table-header rounded-md dark:bg-dark-table-header dark:text-dark-text">
            Ostatnia strona
          </button>
        </div>
      </div>

      <hr class="my-6 border-light-border dark:border-dark-border">

      <div class="flex flex-col md:flex-row justify-between text-lg font-semibold mt-4">
        <p>🚗 KM Łączne: <span class="text-teal-700 dark:text-teal-400">{{ totalKilometers.toFixed(2) }} km</span></p>
        <p>🚗 Kwota Łączna: <span class="text-teal-700 dark:text-teal-400">{{ totalAmount.toFixed(2) }} PLN</span></p>
        <p>📦 Liczba Zleceń: <span class="text-green-700 dark:text-green-400">{{ totalOrders }}</span></p>
      </div>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from '@/stores/authStore';
import AppSidebar from '../AppSidebar.vue';
import companiesService from '@/services/companiesService';
import invoiceService from '@/services/invoiceService';

export default {
  name: 'NewTableComponent',
  components: {
    AppSidebar
  },
  setup() {
    const authStore = useAuthStore();
    authStore.checkExpiration();
    return { user: authStore.user };
  },
  data() {
    return {
      company: {
        id: 0,
        companyName: '',
        companyNip: '',
        companyAddress: '',
        phone: 0,
        orders: [],
        onlyAB: false,
        extendedInvoice: false
      },
      selectedMonthFilter: 'current', // Domyślnie bieżący miesiąc
      selectedYear: new Date().getFullYear(), // Domyślnie bieżący rok
      filteredOrders: [],
      currentPage: 1,
      itemsPerPage: 8,
      totalPages: 0,
      visiblePages: [],
      message: '',
      loading: true,
      months: [
        { value: '0', label: 'Styczeń' },
        { value: '1', label: 'Luty' },
        { value: '2', label: 'Marzec' },
        { value: '3', label: 'Kwiecień' },
        { value: '4', label: 'Maj' },
        { value: '5', label: 'Czerwiec' },
        { value: '6', label: 'Lipiec' },
        { value: '7', label: 'Sierpień' },
        { value: '8', label: 'Wrzesień' },
        { value: '9', label: 'Październik' },
        { value: '10', label: 'Listopad' },
        { value: '11', label: 'Grudzień' },
      ],
      years: Array.from({ length: 1 }, (_, i) => new Date().getFullYear() - i)
    };
  },
  mounted() {
    this.fetchCompany();
  },
  watch: {
    allOrders: {
      immediate: true,
      handler() {
        this.filterOrders();
      }
    },
    totalPages() {
      this.updateVisiblePages();
    }
  },
  computed: {
    totalKilometers() {
      return this.company?.orders?.reduce((sum, order) => sum + (order.orderStatus === 'ZAKONCZONY' ? (order.distanceInKm || 0) : 0), 0) || 0;
    },
    totalAmount() {
      return this.company?.orders?.reduce((sum, order) => sum + (order.orderStatus === 'ZAKONCZONY' ? (order.totalAmount || 0) : 0), 0) || 0;
    },
    totalOrders() {
      return this.company?.orders?.length || 0;
    },
    allOrders() {
      return this.company?.orders?.map(order => ({
        ...order,
        passengerNames: order.passenger && order.passenger.length > 0 ? order.passenger.map(p => `${p.firstName} ${p.lastName}`) : [],
        route: order.passenger && order.passenger.length > 0 
          ? order.passenger.map(p => `${p.pickupAddress} -> ${p.dropoffAddress}`).join(' -> ') 
          : 'Brak danych'
      })) || [];
    },
    paginatedOrders() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredOrders.slice(start, end);
    },
    currentMonthName() {
      if (this.selectedMonthFilter === 'current') {
        return new Intl.DateTimeFormat('pl-PL', { month: 'long' }).format(new Date()).replace(/^\w/, c => c.toUpperCase());
      } else if (this.selectedMonthFilter === 'all') {
        return 'Wszystkie';
      } else {
        return this.months.find(month => month.value === this.selectedMonthFilter)?.label || 'Nie wybrano';
      }
    }
  },
  methods: {
    async fetchCompany() {
      const companyId = this.$route.params.id;
      try {
        const response = await companiesService.getCompanyById(companyId);
        this.company = response.data;
        this.filterOrders();
        this.loading = false;
      } catch (error) {
        this.message = 'Błąd podczas pobierania danych firmy. Spróbuj ponownie.';
        this.loading = false;
      }
    },
    filterOrders() {
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth();

      this.filteredOrders = this.allOrders.filter(order => {
        const orderDate = new Date(order.date);
        if (!order.date || isNaN(orderDate)) return false; // Validate date

        if (this.selectedMonthFilter === 'current') {
          // Filter orders from the current month and year
          const startOfMonth = new Date(currentYear, currentMonth, 1);
          const endOfMonth = new Date(currentYear, currentMonth + 1, 0);
          return orderDate >= startOfMonth && orderDate <= endOfMonth;
        } else if (this.selectedMonthFilter === 'all') {
          // Show all orders for the selected year
          return orderDate.getFullYear() === this.selectedYear;
        } else {
          // Filter orders for the selected month and year
          const monthIndex = parseInt(this.selectedMonthFilter, 10);
          return orderDate.getFullYear() === this.selectedYear && orderDate.getMonth() === monthIndex;
        }
      });
      this.calculateTotalPages();
      this.goToPage(1);
    },
    calculateTotalPages() {
      this.totalPages = Math.ceil(this.filteredOrders.length / this.itemsPerPage);
      this.updateVisiblePages();
    },
    updateVisiblePages() {
      const range = 5;
      const start = Math.max(1, this.currentPage - Math.floor(range / 2));
      const end = Math.min(start + range - 1, this.totalPages);
      this.visiblePages = Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },
    goToPage(page) {
      this.currentPage = page;
      this.updateVisiblePages();
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.updateVisiblePages();
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.updateVisiblePages();
      }
    },
    startEditing(order) {
      console.log('Start editing order:', order);
      alert('Implement startEditing logic!');
    },
    goToProfile(order) {
      const role = this.user.role;
      const routeName = role === 'ROLE_ADMIN' ? 'OrderCompanyProfileAdmin' : 'OrderCompanyProfileDispatcher';
      this.$router.push({
        name: routeName,
        params: { id: order.id },
        query: {
          returnTo: this.$route.name,
          companyId: this.company.id,
        },
      });
    },
    goToInvoices() {
      const companyId = this.$route.params.id;
      const role = this.user.role;
      const routeName = role === 'ROLE_ADMIN' ? 'InvoicesAdmin' : 'InvoicesDispatcher';
      this.$router.push({
        name: routeName,
        params: { id: companyId },
        query: {
          returnTo: this.$route.name,
          companyId: companyId,
        },
      });
    },
    goToPayoffs() {
      const returnTo = this.$route.query.returnTo;
      const currentPage = this.$route.query.currentPage;
      if (returnTo) {
        this.$router.push({ 
          name: returnTo,
          query: { currentPage: currentPage }  
        });
      } else {
        if (this.user.role === 'ROLE_ADMIN') {
          this.$router.push({ name: 'PayoffPageAdmin', query: { currentPage: currentPage } });
        } else {
          this.$router.push({ name: 'PayoffPageDispatcher', query: { currentPage: currentPage } });
        }
      }
    },
    async addInvoice() {
      try {
        const response = await invoiceService.generateInvoice(
          this.company.id,
          this.selectedMonthFilter,
          this.selectedYear,
          this.company.extendedInvoice
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `FV-${this.currentMonthName}-${this.selectedYear}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        // alert(`FV dla ${this.currentMonthName} ${this.selectedYear} została wygenerowana!`);
      } catch (error) {
        this.message = 'Błąd podczas generowania FV.';
        console.error('Error:', error);
      }
    },
    async addSpecification() {
      try {
        const response = await invoiceService.generateSpec(
          this.company.id,
          this.selectedMonthFilter,
          this.selectedYear
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `SPEC-${this.currentMonthName}-${this.selectedYear}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        this.message = 'Błąd podczas generowania Spec.';
        console.error('Error:', error);
      }
    },
    showMessage(msg, type) {
      this.message = msg;
      this.messageType = type === "success" ? "bg-green-500 text-white" : "bg-red-500 text-white";
      this.progress = 0;

      const interval = 30; // Odświeżanie progresu
      const duration = 5000; // Czas wyświetlania (3 sekundy)
      const step = 100 / (duration / interval);

      const progressInterval = setInterval(() => {
        this.progress += step;
        if (this.progress >= 100) {
          clearInterval(progressInterval);
          this.progress = 0;
          this.message = '';
          this.messageType = '';
          this.clearMessage();
        }
      }, interval);
    },
    clearMessage() {
      this.message = '';
      this.messageType = '';
      this.progress = 0;
    },
    getProgressColor() {
      return this.messageType === "bg-green-500 text-white" ? "#16a34a" : "#b91c1c";
    },
  }
};
</script>